import * as React from "react";
import i18n from "i18next";
import { useEffect, useState } from "react";
import { Button } from "reactstrap";
import { EvalObs, EvalPass } from "../../api/EvalWorkflowApi";
import { ParamOption, ParamType } from "../../api/GrammarApi";
import MessageService from "../../service/MessageService";
import Autocomplete from "../autocomplete/Autocomplete";
import GlmId from "../GlmId";
import Tags from "../Tags";
import { isBoneRequired } from "./EvalObsUtils";
import ObsLine from "./ObsLine";

export const GROUND_TRUTH_TAGS = ["POOR_QUALITY", "EXCLUSION"];

interface GroundTruthObsPanelProps {
  bones: Array<ParamOption>;
  obsTypes: Array<string>;
  pass: EvalPass;
  selectedObsIdx: number;

  onCreate(obsType: string, bone: ParamOption): void;

  onSelect(index: number): void;

  onDelete(index: number): void;

  paramTypesByObsTypes?: Record<string, ParamType[]>;

  onParamChange?: (index: number, paramCode: string, value: string) => void;
}

export function GroundTruthObsPanel({
  onCreate,
  onDelete,
  onSelect,
  pass,
  bones,
  selectedObsIdx,
  paramTypesByObsTypes,
  onParamChange,
  obsTypes,
}: GroundTruthObsPanelProps) {
  const [currentObsType, setObsType] = useState<string>(null);

  function isMultiObsType(obsTypes: Array<string>) {
    return obsTypes.length > 1;
  }

  useEffect(() => {
    if (!isMultiObsType(obsTypes)) {
      setObsType(obsTypes[0]);
    }
  }, [obsTypes]);

  const [currentBoneOpt, setBoneOpt] = useState<ParamOption>(null);

  const handleCreation = (e: React.FormEvent) => {
    e.preventDefault();
    const isBoneOptionValid = !isBoneRequired(currentObsType) || currentBoneOpt;
    if (!currentObsType) {
      MessageService.error(i18n.t("error.selectObsType"));
      return;
    } else if (!isBoneOptionValid) {
      MessageService.error(i18n.t("error.selectBone"));
      return;
    }

    onCreate(currentObsType, currentBoneOpt);
    if (isMultiObsType(obsTypes)) {
      setObsType(null);
    }
    setBoneOpt(null);
  };

  function handleObsTypeChange(obsType: string) {
    if (!isBoneRequired(obsType)) {
      setBoneOpt(null);
    }
    return setObsType(obsType);
  }

  return (
    <div className="card">
      <div className="card-title">{i18n.t("global.observation.s")}</div>
      <div className="card-body">
        <form onSubmit={handleCreation}>
          {obsTypes.length > 1 && (
            <Autocomplete
              onChange={(obsType) => handleObsTypeChange(obsType)}
              value={currentObsType}
              placeholder={`${i18n.t("global.observation.type")} ...`}
              items={obsTypes}
              toLabel={(obsType) => obsType}
              renderItem={(obsType) => <span>{obsType}</span>}
            />
          )}

          {isBoneRequired(currentObsType) && (
            <Autocomplete
              onChange={(boneOpt) => setBoneOpt(boneOpt)}
              placeholder={`${i18n.t("global.observation.boneCode")} ...`}
              items={bones}
              value={currentBoneOpt}
              toLabel={(boneOpt) => boneOpt.label}
              renderItem={(boneOpt) => <span>{boneOpt.label}</span>}
            />
          )}

          <Button>{i18n.t("global.observation.add")}</Button>
        </form>

        {pass.observations?.map((obs, idx) => (
          <ObsLine
            key={idx}
            obs={obs}
            bones={bones}
            selected={idx === selectedObsIdx}
            onClick={() => onSelect(idx)}
            onDelete={() => onDelete(idx)}
            paramTypes={paramTypesByObsTypes[obs.obsType]}
            onParamSelect={(paramCode, value) => onParamChange?.(idx, paramCode, value)}
          />
        ))}
      </div>
    </div>
  );
}

export type Patient = { patientAge?: number; patientSex?: string };

export function GroundTruthPatient(props: Patient) {
  return (
    <div className="card">
      <div className="card-title">{i18n.t("global.patient")}</div>
      <div className="card-body">
        {i18n.t("global.age")}: {props.patientAge} | {i18n.t("global.sex")} : {props.patientSex}
        <br />
      </div>
    </div>
  );
}

export interface StudyTags {
  tags: Array<string>;
  studyId: string;

  onChange(newTags: Array<string>): void;
}

export function GroundTruthTags(props: StudyTags) {
  return (
    <div className="card">
      <div className="card-title">Tags</div>
      <div className="card-body">
        <Tags
          tags={props.tags}
          availableTags={GROUND_TRUTH_TAGS}
          onTagsChanged={(newTags) => props.onChange(newTags)}
        />
        <div>
          StudyId : <GlmId value={props.studyId} />
        </div>
      </div>
    </div>
  );
}

function displayBoneLabel(boneCode: string, bones: ParamOption[] = []) {
  if (bones.length === 0) {
    return boneCode;
  }
  const bone = bones.find((bone) => bone.code === boneCode);
  return bone ? bone.label : boneCode;
}

export function EvalObservation({ obs, bones }: { obs: EvalObs; bones?: ParamOption[] }) {
  return (
    <div className="card">
      <div className="card-title">
        Obs {obs._badge.label} : {obs.obsType} {obs.boneCode && displayBoneLabel(obs.boneCode, bones)}
      </div>

      <div className="card-body">
        <div>Nb regions : {obs.regions.length}</div>
        {Object.keys(obs.valueByParam).length > 0 && (
          <>
            {Object.entries(obs.valueByParam).map(([key, value]) => (
              <div key={key}>
                {key} : {value}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
